import type {
  BudgetItem,
  Guest,
  RoomElement,
  SeatingPlan,
  Table,
  Task,
  User,
  Wedding,
} from "@amazing-wedding/shared-types";
import type { SubscriptionSnapshot } from "./createSharedSubscription";

/**
 * Stable empty arrays — never mutate.
 *
 * Frozen at runtime so accidental push/splice throws instead of silently
 * corrupting the shared sentinel. The static type stays as `T[]` (not
 * `ReadonlyArray<T>`) to match `SubscriptionSnapshot<T[]>.data` consumers
 * that pass these around as plain arrays; the runtime freeze is the
 * actual guard.
 */
const frozenEmpty = <T>(): T[] => Object.freeze([] as T[]) as unknown as T[];

export const EMPTY_GUESTS: Guest[] = frozenEmpty<Guest>();
export const EMPTY_TASKS: Task[] = frozenEmpty<Task>();
export const EMPTY_BUDGET_ITEMS: BudgetItem[] = frozenEmpty<BudgetItem>();
export const EMPTY_SEATING_PLANS: SeatingPlan[] = frozenEmpty<SeatingPlan>();
export const EMPTY_TABLES: Table[] = frozenEmpty<Table>();
export const EMPTY_ROOM_ELEMENTS: RoomElement[] = frozenEmpty<RoomElement>();

export const WEDDING_SNAPSHOT_SIGNED_OUT: SubscriptionSnapshot<Wedding | null> = {
  data: null,
  loading: false,
};

export const WEDDING_SNAPSHOT_SERVER: SubscriptionSnapshot<Wedding | null> = {
  data: null,
  loading: true,
};

export const GUESTS_SNAPSHOT_LOADING: SubscriptionSnapshot<Guest[]> = {
  data: EMPTY_GUESTS,
  loading: true,
};

export const GUESTS_SNAPSHOT_EMPTY: SubscriptionSnapshot<Guest[]> = {
  data: EMPTY_GUESTS,
  loading: false,
};

export const TASKS_SNAPSHOT_LOADING: SubscriptionSnapshot<Task[]> = {
  data: EMPTY_TASKS,
  loading: true,
};

export const TASKS_SNAPSHOT_EMPTY: SubscriptionSnapshot<Task[]> = {
  data: EMPTY_TASKS,
  loading: false,
};

export const BUDGET_SNAPSHOT_LOADING: SubscriptionSnapshot<BudgetItem[]> = {
  data: EMPTY_BUDGET_ITEMS,
  loading: true,
};

export const BUDGET_SNAPSHOT_EMPTY: SubscriptionSnapshot<BudgetItem[]> = {
  data: EMPTY_BUDGET_ITEMS,
  loading: false,
};

export const SEATING_PLANS_SNAPSHOT_LOADING: SubscriptionSnapshot<SeatingPlan[]> = {
  data: EMPTY_SEATING_PLANS,
  loading: true,
};

export const SEATING_PLANS_SNAPSHOT_EMPTY: SubscriptionSnapshot<SeatingPlan[]> = {
  data: EMPTY_SEATING_PLANS,
  loading: false,
};

export const TABLES_SNAPSHOT_LOADING: SubscriptionSnapshot<Table[]> = {
  data: EMPTY_TABLES,
  loading: true,
};

export const TABLES_SNAPSHOT_EMPTY: SubscriptionSnapshot<Table[]> = {
  data: EMPTY_TABLES,
  loading: false,
};

export const ROOM_ELEMENTS_SNAPSHOT_LOADING: SubscriptionSnapshot<RoomElement[]> = {
  data: EMPTY_ROOM_ELEMENTS,
  loading: true,
};

export const ROOM_ELEMENTS_SNAPSHOT_EMPTY: SubscriptionSnapshot<RoomElement[]> = {
  data: EMPTY_ROOM_ELEMENTS,
  loading: false,
};

export const USER_SNAPSHOT_SIGNED_OUT: SubscriptionSnapshot<User | null> = {
  data: null,
  loading: false,
};

export const USER_SNAPSHOT_SERVER: SubscriptionSnapshot<User | null> = {
  data: null,
  loading: true,
};
